import { memo, useMemo, useRef } from "react";
import { AgGrid, AgGridReact, Flex, Grid } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { globalEnv } from "~/global-env";
import { useGetCurrentUserQuery } from "~/graphql/mainHooks";
import { HeaderWithTooltip } from "~/pages/Compliance/Common/HeaderWithTooltip";
import { LoadingCellRenderer } from "~/pages/Compliance/Common/LoadingCellRenderer";
import { ONBOARDING_STATUS_FILTER_VALUES } from "~/pages/Compliance/Common/OnboardingStatus/resolveOnboardingState";
import { onboardingCellClass } from "~/pages/Compliance/Common/OnboardingStatus/styles_vanilla.css";
import { useRefreshOnLinkCaseClose } from "~/pages/Compliance/Common/OnboardingStatus/useRefreshOnLinkCaseClose";
import { ProgramTagRenderer } from "~/pages/Compliance/Common/ProgramTagRenderer";
import { ExternalLinkCountCellRenderer } from "~/pages/Compliance/CompliancePositionsTable/ExternalLinkCountCellRenderer";
import { ComplianceSettingsDrawer } from "~/pages/Compliance/ComplianceSettingsDrawer";
import { PROGRAM_OPTIONS } from "~/pages/Compliance/ComplianceSettingsDrawer/programOptions";
import { useComplianceClientSettingsDrawer } from "~/pages/Compliance/ComplianceSettingsDrawer/useComplianceSettingsDrawer";
import { isComplianceManager } from "~/utils/userRoles";
import { dashboardContainer } from "../styles_vanilla.css";
import { ComplianceClientsGraphqlDataSource } from "./complianceClientsGraphqlDataSource";
import { OnboardingStatusRenderer } from "./OnboardingStatusRenderer";
import { RowItem } from "./types";

const DEFAULT_PAGE_SIZE = 20;

export const ComplianceClientsTable = memo(() => {
  const complianceClientsDatasource = useMemo(
    () => new ComplianceClientsGraphqlDataSource(),
    [],
  );
  const components = useMemo(() => ({ headerWithTooltip: HeaderWithTooltip }), []);
  const gridApiRef = useRef<AgGrid.GridApi | null>(null);
  // The tab is already behind complianceEntityOnboardingEnabled via its route
  const settingsDrawer = useComplianceClientSettingsDrawer();

  // Only compliance managers can edit settings, so only they get the row-click drawer
  const { data: currentUserData } = useGetCurrentUserQuery({
    context: withClientContext("staff"),
  });
  const roles = (currentUserData?.currentUser?.roles?.edges ?? [])
    .map((edge) => edge.node.name)
    .filter((name): name is string => !!name);
  const canEditSettings = isComplianceManager(roles);

  const onGridReady = (event: AgGrid.GridReadyEvent) => {
    gridApiRef.current = event.api;
  };

  // SSRM caches rows, so a saved change refetches the grid rather than the Apollo cache
  const refreshGrid = () => gridApiRef.current?.refreshServerSide({ purge: true });

  useRefreshOnLinkCaseClose(gridApiRef, "/clients/link-case");

  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
    rowHeight: 42,
    suppressCellFocus: true,
    statusBar: {
      statusPanels: [{ statusPanel: "agTotalRowCountComponent" }],
    },
    rowModelType: "serverSide",
    serverSideDatasource: complianceClientsDatasource,
    getRowId: (params) => params.data?.id?.toString(),
  };

  const colDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
    {
      field: "client",
      headerName: "Client",
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains"],
        maxNumConditions: 1,
      },
      width: 300,
      pinned: "left",
    },
    {
      field: "entities",
      headerName: "Entities",
      cellRenderer: (params: { value: RowItem["entities"]; data?: RowItem }) => (
        <ExternalLinkCountCellRenderer
          count={params.value}
          launchLink={`${globalEnv.complianceRoute}/entities?clientId=${params.data?.id}`}
          tooltipContent="View entities"
          tooltipId="view-entities-tooltip"
          testIdPrefix="view-entities"
        />
      ),
      filter: false,
      cellStyle: { display: "flex", alignItems: "center" },
      width: 148,
    },
    {
      field: "status",
      headerName: "Status",
      cellRenderer: (params: { value: RowItem["status"] }) => params.value ?? "--",
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["Active", "Inactive"],
      },
      width: 154,
    },
    {
      field: "productLine",
      headerName: "Product line",
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["Fund Administration", "Software"],
      },
      width: 138,
    },
    {
      field: "clientProgram",
      headerComponent: "headerWithTooltip",
      headerComponentParams: {
        displayName: "Client program",
        tooltipContent: "The program the client must comply with.",
      },
      cellRenderer: (params: { value: RowItem["clientProgram"] }) => (
        <ProgramTagRenderer program={params.value} testId="client-program-tag" />
      ),
      filter: "agSetColumnFilter",
      filterParams: {
        values: PROGRAM_OPTIONS.map((option) => option.label),
      },
      width: 605,
    },
    {
      field: "onboardingStatus",
      headerName: "Onboarding status",
      cellRenderer: OnboardingStatusRenderer,
      // FillCell-style class strips cell padding so the Action-required fill reaches the edges.
      cellClass: onboardingCellClass,
      // Server-side set filter over the fixed onboarding buckets the slice returns.
      filter: "agSetColumnFilter",
      filterParams: { values: [...ONBOARDING_STATUS_FILTER_VALUES] },
      sortable: true,
      width: 328,
    },
  ];

  return (
    <Flex className={dashboardContainer}>
      <Grid
        gridOptions={gridOptions}
        columnDefs={colDefs}
        components={components}
        suppressColumnVirtualisation={true}
        suppressRowVirtualisation={true}
        loadingCellRenderer={LoadingCellRenderer}
        pagination
        paginationPageSizeSelector={false}
        paginationPageSize={DEFAULT_PAGE_SIZE}
        rowModelType="serverSide"
        serverSideDatasource={complianceClientsDatasource}
        cacheBlockSize={DEFAULT_PAGE_SIZE}
        onGridReady={onGridReady}
        onRowClicked={canEditSettings ? settingsDrawer.onRowClicked : undefined}
      />
      <ComplianceSettingsDrawer
        subject="client"
        row={settingsDrawer.row}
        onClose={settingsDrawer.onClose}
        onSaved={refreshGrid}
      />
    </Flex>
  );
});
ComplianceClientsTable.displayName = "ComplianceClientsTable";
